import {
  ShieldCheck, Palette, Building2, Download, Users, Workflow, Truck,
  Shield, Mail, Calculator, Warehouse, FileSpreadsheet, Clock, Satellite,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { Navigate } from "react-router-dom";
import SettingsFold from "../components/SettingsFold";
import ServiceNowCard from "../components/ServiceNowCard";
import AaWorkformsCard from "../components/AaWorkformsCard";
import AaTowPricingCard from "../components/AaTowPricingCard";
import SnTowPricingCard from "../components/SnTowPricingCard";
import ManualTowPricingCard from "../components/ManualTowPricingCard";
import PoliceImpoundConfigCard from "../components/PoliceImpoundConfigCard";
import StorageRatesCard from "../components/StorageRatesCard";
import BusinessInfoCard from "../components/BusinessInfoCard";
import CompanyBrandingCard from "../components/CompanyBrandingCard";
import OnCallCard from "../components/OnCallCard";
import TelematicsGuruCard from "../components/TelematicsGuruCard";
import DangerZoneCard from "../components/DangerZoneCard";
import EmailIntakeCard from "../components/EmailIntakeCard";
import UserManagementCard from "../components/UserManagementCard";
import InvoiceExportCard from "../components/InvoiceExportCard";
import { DataExportCard } from "../components/DataExportCard";

/**
 * Settings / Integrations orchestrator.
 *
 * Two columns, everything folded shut. It used to be sixteen open forms in
 * one column and the operator was scrolling for a minute to reach Invoice
 * Export. Pairing is deliberate: a partner's feed sits beside that partner's
 * rate card, so the thing you came to change is next to its context.
 *
 * RBAC: dispatchers see only their own on-call schedule plus a notice;
 * everything else is admin-only. Each card is self-contained.
 */
function Group({ title, blurb, children }) {
  return (
    <>
      <div className="lg:col-span-2 pt-4 first:pt-0">
        <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          {title}
        </h2>
        {blurb && (
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{blurb}</p>
        )}
      </div>
      {children}
    </>
  );
}

export default function SettingsPage() {
  const { isAdmin, user } = useAuth();
  if (user === false) return <Navigate to="/login" replace />;
  const dispatcherView = user && !isAdmin;

  return (
    <div className="p-6 space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-2">
          <ShieldCheck className="h-7 w-7 text-blue-600" />
          Integrations
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Everything you set once and forget. Click a heading to open it.
        </p>
      </div>

      {dispatcherView ? (
        <>
          <OnCallCard />
          <section
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-6"
            data-testid="settings-dispatcher-notice"
          >
            <h2 className="font-display text-lg font-black text-slate-800 dark:text-slate-100">
              Integrations
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Telematics Guru, ServiceNow / First Rescue and AA Workforms credentials are
              admin-only. Ask your admin to update those.
            </p>
          </section>
        </>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4 items-start">
          <Group title="Your business">
            <SettingsFold
              id="branding" icon={Palette} title="Company Branding"
              description="Logo, colours and the name on everything you print."
            >
              <CompanyBrandingCard />
            </SettingsFold>
            <SettingsFold
              id="business-info" icon={Building2} title="Business Letterhead"
              description="Address, phone, GST number — the details on your paperwork."
            >
              <BusinessInfoCard />
            </SettingsFold>
          </Group>

          <Group title="Partner feeds and their rates" blurb="Each partner's job feed sits beside the rate card they dictate.">
            <SettingsFold
              id="servicenow" icon={Workflow} title="ServiceNow"
              description="First Rescue's job feed — credentials and polling."
            >
              <ServiceNowCard />
            </SettingsFold>
            <SettingsFold
              id="sn-pricing" icon={Calculator} title="First Rescue Pricing"
              description="The rate card First Rescue pays you on."
            >
              <SnTowPricingCard />
            </SettingsFold>
            <SettingsFold
              id="aa-workforms" icon={Truck} title="AA Roadservice"
              description="workforms.link sign-in, claiming and the live job feed."
            >
              <AaWorkformsCard />
            </SettingsFold>
            <SettingsFold
              id="aa-pricing" icon={Calculator} title="AA Tow Pricing"
              description="The rate card AA pays you on."
            >
              <AaTowPricingCard />
            </SettingsFold>
          </Group>

          <Group title="Your own pricing and intake" blurb="These are yours — the Tow Calc and every impound quote read them.">
            <SettingsFold
              id="police-impound" icon={Shield} title="Police Impound Pricing"
              description="Tow fees, 28-day and 6-month bundles, overstay per day."
            >
              <PoliceImpoundConfigCard />
            </SettingsFold>
            <SettingsFold
              id="email-intake" icon={Mail} title="Email Intake"
              description="Mailboxes that turn incoming email into jobs."
            >
              <EmailIntakeCard />
            </SettingsFold>
            <SettingsFold
              id="manual-pricing" icon={Calculator} title="Manual Tow Pricing"
              description="Your own rate card for direct customer quotes."
            >
              <ManualTowPricingCard />
            </SettingsFold>
            <SettingsFold
              id="storage-rates" icon={Warehouse} title="Storage Rates"
              description="Per-day yard rates, applied live to everything stored."
            >
              <StorageRatesCard />
            </SettingsFold>
          </Group>

          <Group title="Money out the door">
            <SettingsFold
              id="invoice-export" icon={FileSpreadsheet} title="Invoice Export"
              description="Invoice numbering, GST rate name and account codes for Xero / MYOB."
            >
              <InvoiceExportCard />
            </SettingsFold>
          </Group>

          <Group title="Cover and tracking">
            <SettingsFold
              id="on-call" icon={Clock} title="After-Hours / On-Call"
              description="Who's covering tonight, and the hours that count as after-hours."
            >
              <OnCallCard />
            </SettingsFold>
            <SettingsFold
              id="telematics" icon={Satellite} title="GPS Tracking"
              description="Telematics Guru feed for live truck positions."
            >
              <TelematicsGuruCard />
            </SettingsFold>
          </Group>

          <Group title="People and records">
            <SettingsFold
              id="users" icon={Users} title="Users & Access"
              description="Add your team and decide what each of them can do."
            >
              <UserManagementCard />
            </SettingsFold>
            <SettingsFold
              id="data-export" icon={Download} title="Your Data"
              description="Download everything on your account as a zip."
            >
              <DataExportCard />
            </SettingsFold>
          </Group>

          <Group title="Danger zone">
            <SettingsFold
              id="danger" icon={AlertTriangle} title="Danger Zone" tone="danger"
              description="Deleting test data and photo retention. Read twice."
            >
              <DangerZoneCard />
            </SettingsFold>
          </Group>
        </div>
      )}

      {/* In-app support contact — only reachable once signed in, so the
          address is never exposed on the public landing page (spam guard). */}
      <section
        className="border-t border-slate-200 dark:border-slate-700 pt-5 text-sm text-slate-500 dark:text-slate-400"
        data-testid="settings-support-contact"
      >
        Need a hand? Email our team at{" "}
        <a
          href="mailto:support@tow-hook.com"
          className="font-semibold text-slate-700 dark:text-slate-200 hover:text-blue-600 underline underline-offset-2"
          data-testid="support-email-link"
        >
          support@tow-hook.com
        </a>
      </section>
    </div>
  );
}
